import React, { useEffect, useState } from "react";
import { isNumberString } from "class-validator";
import { trim } from "lodash";
import { useFormStore } from "@modules/formx-react/useFormStore";
import { GetInvoice, InvoiceFragment } from "@graphql";
import { useDebounced, useStateRef } from "@hooks";
import locale from "@bonliva-crm/locale";
import { FormErrors } from "@modules/formx";
import { BookingInvoiceDialog } from "./BookingInvoiceDialog";
import { InvoiceDialogFormData } from "./utils";
import { InvoiceMonth, InvoiceSplitType } from "@bonliva-crm/shared";
import { useInvoiceProvider } from "../useInvoiceProvider";

type Options = {
  withCustomNumber: boolean;
  costCenterId: string;
  onSubmit: (value: InvoiceDialogFormData) => void;
};

const LIMIT_SIX_CHARACTER = 6;

export function useBookingInvoiceDialog(options: Options) {
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useStateRef<InvoiceFragment | null>(null);
  const invoiceProvider = useInvoiceProvider(options.costCenterId);

  const { debounced: handleInvoiceNumber } = useDebounced((invoiceNumber: string) => {
    invoiceNumber = (invoiceNumber || "").replace(/[^0-9]/g, "");
    setCurrentInvoice(null);

    if (!invoiceNumber) {
      return;
    }

    setLoading(true);
    form.values.invoiceNumberShort = "";

    const truncateInvoiceNumber = invoiceNumber.substring(0, LIMIT_SIX_CHARACTER);

    GetInvoice.func(
      {
        number: truncateInvoiceNumber,
        provider: invoiceProvider
      },
      { fetchPolicy: "network-only" }
    )
      .then(({ data }) => {
        form.values.invoiceNumberShort = data.invoiceByNumber.InvoiceNumber;
        setCurrentInvoice(data.invoiceByNumber);
      })
      .finally(() => {
        setLoading(false);
      });
  });

  const form = useFormStore<InvoiceDialogFormData>({
    initialValues: {
      invoiceNumber: "",
      invoiceNumberShort: "",
      month: InvoiceMonth.Full,
      type: InvoiceSplitType.ByShift
    },
    onSubmit: () => {
      const value = trim(form.values.invoiceNumber);

      if (!value) {
        return Promise.resolve();
      }

      options.onSubmit(form.values);
      setShowDialog(false);
      return Promise.resolve();
    },
    onSetValue: ({ name, value }) => {
      if (name === "invoiceNumber") {
        handleInvoiceNumber(value);
      }
    },
    exposeAs: "bookingInvoiceDialog",
    validate: (form) => {
      if (!form) {
        return;
      }

      const { invoiceNumber } = form.values;
      const errors: FormErrors<InvoiceDialogFormData> = {};

      if (!invoiceNumber || !isNumberString(invoiceNumber)) {
        errors.invoiceNumber = locale.InvoiceNumber;
      }

      return errors;
    }
  });

  const isInvoiceFound = !form.values.invoiceNumber ? true : !!currentInvoice;
  const canSubmit = form.canSubmit && isInvoiceFound;

  const open = () => {
    setShowDialog(true);
    setCurrentInvoice(null);
    setLoading(false);
    form.reset(
      {
        invoiceNumber: "",
        invoiceNumberShort: "",
        month: InvoiceMonth.Full,
        type: InvoiceSplitType.ByShift
      },
      true
    );
  };

  useEffect(() => {
    form.reset({
      invoiceNumber: "",
      invoiceNumberShort: "",
      month: InvoiceMonth.Full,
      type: InvoiceSplitType.ByShift
    });
  }, [showDialog]);

  const dialog = showDialog && (
    <BookingInvoiceDialog
      form={form}
      canSubmit={canSubmit}
      loading={loading}
      currentInvoice={currentInvoice}
      isInvoiceFound={isInvoiceFound}
      onClose={() => setShowDialog(false)}
      withCustomNumbers={options.withCustomNumber}
      submitCustom={(v) => {
        options.onSubmit({
          invoiceNumber: v,
          invoiceNumberShort: v,
          month: InvoiceMonth.Full,
          type: InvoiceSplitType.ByShift
        });
        setShowDialog(false);
      }}
    />
  );

  return {
    form,
    canSubmit,
    isInvoiceFound,
    loading,
    currentInvoice,
    dialog,
    open
  };
}
